import React, {useEffect, useState} from 'react';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {useTranslation} from 'react-i18next';
import BoardRound from './boardRound';
import _ from 'lodash';
import InformationBox from '../../../../components/other/informationBox';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import moment from 'moment/moment';
import config from '../../../../app/config';
import makeStyles from '@mui/styles/makeStyles';
import uuidv4 from 'uuid/v4';
import Notes from '../../../../util/notes';
import {useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';

const useStyles = makeStyles((theme) => ({
    accordionTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 16,
        fontWeight: 700,
    },
    chip: {
        padding: 5,
        border: '1px solid #626466',
        borderRadius: 5,
        fontSize: 10,
        fontWeight: 400,
    },
}));

const BoardRoundList = (props) => {
    const {t} = useTranslation();
    const classes = useStyles();

    const [mode, setMode] = useState('list');

    const [list, setList] = useState([]);
    const [form, setForm] = useState(null);

    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    // List data
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        if (props.onCustomButtons) {
            const buttons = [];
            if (mode === 'list') {
                buttons.push(
                    <AgyleButton
                        colour="primary"
                        onClick={() => setMode('newRecord')}
                    >
                        {t('Add new record')}
                    </AgyleButton>,
                );
            }
            props.onCustomButtons(buttons);
        }
        if (props.onSubmitFunction) {
            props.onSubmitFunction(handleSave);
        }
        if (props.onDisableSubmit) {
            // props.onDisableSubmit();
        }
        if (props.onHideSubmit) {
            props.onHideSubmit(mode === 'list');
        }
    }, [mode, form, list]);

    useEffect(() => {
        setList(props.patient?.boardRound?.list || []);
    }, [props.patient]);

    const handleSave = () => {
        if (!_.isNil(form)) {
            const dateOfForm = moment().toISOString();

            const id = uuidv4() + '';

            let data = [{
                name: 'boardRound',
                value: {
                    ...form,
                    id: id,
                    date: dateOfForm,
                    by: userFullName,
                    login: userObject?.userName,
                    parentForm: props.parentForm,
                },
                noteToAddWithRefId: Notes.getBoardRoundNote(form),
                referenceId: id,
            },
            ];

            const isNewPatientData =
                (!props.patient.intendentManagement && (form.edd || form.cdd)) ||
                (props.patient.intendentManagement &&
                    (form.edd !== props.patient?.intendentManagement?.edd ||
                        form.cdd !== props.patient?.intendentManagement?.cdd));

            if (isNewPatientData) {
                let newIntendentManagement = {
                    ...props.patient?.intendentManagement,
                    edd: form.edd,
                    cdd: form.cdd,
                    who: userFullName,
                    date: dateOfForm,
                    login: userObject?.userName,
                };

                data.push({
                    name: 'intendentManagement',
                    value: newIntendentManagement,
                    noteToAdd: Notes.getIntendentManagementNote(newIntendentManagement),
                });
            }
            saveData(data);
        } else {
            return false;
        }
    };

    const validateObject = (obj) => {
        if (_.isArray(obj) && !_.isEmpty(obj)) {
            return obj[0].hasOwnProperty('value') && obj[0].hasOwnProperty('noteToAddWithRefId')
                && obj[0].hasOwnProperty('referenceId');
        }
        return false;
    };

    const saveData = (obj) => {
        if (!_.isEmpty(obj) && validateObject(obj)) {
            const cloneList = _.cloneDeep(list);
            cloneList.unshift(obj[0].value);
            setList(cloneList);

            let data = [{
                name: 'boardRound',
                value: {
                    list: [...cloneList],
                    by: userFullName,
                    login: userObject?.userName,
                    date: moment().toISOString(),
                },
                noteToAddWithRefId: [{
                    noteToAddWithRefId: obj[0].noteToAddWithRefId,
                    referenceId: obj[0].referenceId,
                }],
                idToTrack: obj[0]?.idToTrack || null,
            }];
            if (obj.length > 1) {
                data = data.concat(obj.slice(1));
            }
            props.onChange(data);
        } else {
            return false;
        }
    };

    const handleExpanded = (id) => {
        const exp = {...expanded};
        if (exp[id]) delete exp[id];
        else exp[id] = true;
        setExpanded(exp);
    };

    const renderChips = (element) => {
        let chips = [];
        if (element.timeBoardRound) chips.push(<span className={classes.chip}>{element.timeBoardRound}</span>);
        if (element.typeBoardRound) chips.push(<span className={classes.chip}>{element.typeBoardRound}</span>);
        if (element.optimisedToLeave) chips.push(<span className={classes.chip}>Optimised</span>);
        if (element.dischargeToday) chips.push(<span className={classes.chip}>Discharge Today</span>);
        if (element.greenDay) chips.push(<span className={classes.chip}>Green Day</span>);
        return chips;
    };

    const renderListView = () => {
        if (_.isEmpty(list)) {
            return (
                <InformationBox type="info">
                    {t('There is no data to display.')}
                </InformationBox>
            );
        }

        return (
            <div style={{position: 'relative'}}>
                {!_.isEmpty(list) && list.map(element => (
                    <AgyleAccordion
                        key={element.id}
                        expanded={!expanded[element?.id]}
                        onChange={() => handleExpanded(element?.id)}
                        label={
                            <div className={classes.accordionTitle}>
                                {moment(element.date).format(config.defaultDateTimeFormat)} {renderChips(element)}
                            </div>
                        }
                        formBackground
                    >
                        <InformationBox type="info" label="Conclusion">
                            <div style={{fontWeight: 'normal'}}>
                                {element.edd && <div> EDD: {moment(element.edd).format(config.defaultDateFormat)} </div>}
                                {element.cdd && <div> CDD: {moment(element.cdd).format(config.defaultDateFormat)} </div>}
                                <br/>
                                {element.conclusion}
                            </div>
                        </InformationBox>
                    </AgyleAccordion>
                ))}
            </div>
        );
    };

    const renderView = () => {
        if (mode === 'list') {
            return renderListView();
        } else {
            return <BoardRound changeFormData={setForm} {...props}/>;
        }
    };

    return (
        <>
            {renderView()}
        </>
    );
};

export default BoardRoundList;